import type { RolePermissionDTO } from '../../../types/team.types';
import { formatRole } from '../../../util/role-display';

export type RolesPermissionColumn = {
  id: number;
  name: string;
  dataIdentifier: string;
};

export type RolesPermissionEntry = {
  id: number;
  actionLabel: string;
  permissions: Record<string, boolean>;
};

export type RolesPermissionGroup = {
  id: number;
  type: string;
  permissionData: RolesPermissionEntry[];
};

// Backend (user-service ResourcePermissionRolesServiceImpl) emits Keycloak
// scope names verbatim under resourcePermissionsMap. Only these four verbs
// have corresponding entries in permissionRolesMap; any other scope name
// (e.g. custom Keycloak scopes) renders as a row with no data and is
// filtered out here rather than shown as a row of all-X cells.
const ACTION_LABELS: Record<string, string> = {
  GET: 'View',
  POST: 'Add',
  PUT: 'Edit',
  DELETE: 'Delete',
};

const ACTION_ORDER: readonly string[] = ['GET', 'POST', 'PUT', 'DELETE'];

// permissionRolesMap key format is "VERB :Resource" (note the literal space
// before the colon) — see user-service ResourcePermissionRolesServiceImpl.
// Both sides intentionally agree on this format.
const buildPermissionKey = (action: string, target: string) =>
  `${action} :${target}`;

// Roles that don't appear in any permissionRolesMap entry would render as
// a column of all-denied cells (lookup returns undefined → falsy → X).
// Drop them to avoid the misleading "this role can do nothing" appearance.
const collectActiveRoleCodes = (
  permissionRolesMap: RolePermissionDTO['permissionRolesMap'],
): Set<string> => {
  const codes = new Set<string>();
  for (const map of Object.values(permissionRolesMap)) {
    for (const code of Object.keys(map)) {
      codes.add(code);
    }
  }
  return codes;
};

export const processPermissionData = (
  data: RolePermissionDTO,
): { groups: RolesPermissionGroup[]; columns: RolesPermissionColumn[] } => {
  const activeRoleCodes = collectActiveRoleCodes(data.permissionRolesMap);

  const columns: RolesPermissionColumn[] = Object.entries(data.roles)
    .filter(([code]) => activeRoleCodes.has(code))
    .map(([code, description], index) => ({
      id: index,
      name: formatRole(code, description),
      dataIdentifier: code,
    }));

  const groups: RolesPermissionGroup[] = Object.keys(
    data.resourcePermissionsMap,
  ).map((resourceKey, groupIndex) => {
    const scopes = data.resourcePermissionsMap[resourceKey][0] ?? {};

    const permissionData: RolesPermissionEntry[] = ACTION_ORDER.filter(
      (action) => action in scopes,
    ).map((action, entryIndex) => ({
      id: entryIndex,
      actionLabel: ACTION_LABELS[action],
      permissions: data.permissionRolesMap[buildPermissionKey(action, scopes[action])] ?? {},
    }));

    return { id: groupIndex, type: resourceKey, permissionData };
  });

  return { groups, columns };
};
